import React, { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import ConstituentMerge, { ScheduleAutoMergeTool } from './ConstituentMerge'
import AgedRecordRemoval from './AgedRecordRemoval'
import Screening from './Screening'
import BuildSegment from './BuildSegment'
import WealthScreening from './WealthScreening'
import DemographicScreening from './DemographicScreening'
import { CREDIT_PRICES, formatUsageDollars, getUsage, addUsageCredits } from '../usage'

function NavItem({ icon, label, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: '9px',
      width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer',
      padding: '10px 10px', borderRadius: '8px', marginBottom: '1px',
      minHeight: '44px',
      background: active ? 'rgba(29,111,219,0.09)' : 'transparent',
      color: active ? '#1d6fdb' : '#4b5563',
      fontSize: '13px', fontWeight: active ? '600' : '400',
      fontFamily: "'Inter', sans-serif",
    }}>
      <span style={{ fontSize: '16px', width: '22px', textAlign: 'center', flexShrink: 0, lineHeight: 1, opacity: active ? 1 : 0.7 }}>{icon}</span>
      {label}
    </button>
  )
}

function LoadingStatValue() {
  return <span style={styles.loadingStatValue} aria-label="Loading stat" />
}

function Dashboard({ session, orgData }) {
  const [apiStatus, setApiStatus] = useState('checking')
  const [activePage, setActivePage] = useState('dashboard')
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [mergeStats, setMergeStats] = useState(null)
  const [agedStats, setAgedStats] = useState(null)
  const [contactStats, setContactStats] = useState(null)
  const [agedStatsLoading, setAgedStatsLoading] = useState(false)
  const [contactStatsLoading, setContactStatsLoading] = useState(false)
  const [optimizeLoading, setOptimizeLoading] = useState(false)
  const [optimizeResult, setOptimizeResult] = useState(null)
  const [quickMergeLoading, setQuickMergeLoading] = useState(false)
  const [quickMergeResult, setQuickMergeResult] = useState(null)
  const [usageCredits, setUsageCredits] = useState(null)
  const [usageAllowance, setUsageAllowance] = useState(0)

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const handleLogout = async () => {
    await supabase.auth.signOut()
  }

  const navigateTo = (page) => {
    setActivePage(page)
    if (isMobile) setSidebarOpen(false)
  }

  const tessituraHeaders = {
    'x-tessitura-auth': orgData.organizations.tessitura_auth_string,
    'x-tessitura-url': orgData.organizations.tessitura_base_url,
    'Content-Type': 'application/json'
  }

  const callTessitura = async (endpoint, method = 'GET', body = null) => {
    const response = await fetch(`/api/tessitura?endpoint=${encodeURIComponent(endpoint)}`, {
      method,
      headers: tessituraHeaders,
      body: body ? JSON.stringify(body) : undefined
    })
    const text = await response.text()
    return { ok: response.ok, status: response.status, text }
  }

  const callTessituraProcedure = async (body) => {
    const first = await callTessitura('Custom/Execute', 'POST', body)
    if (!first.ok && first.status === 400 && body?.ProcedureId) {
      const fallbackBody = { ...body }
      delete fallbackBody.ProcedureId
      return callTessitura('Custom/Execute', 'POST', fallbackBody)
    }
    return first
  }

  const getTessituraErrorMessage = (fallback, response) => {
    const statusText = response?.status ? ` Tessitura returned HTTP ${response.status}.` : ''
    const noDetails = `${fallback}${statusText} No error details were returned.`
    const text = response?.text
    if (!text) return noDetails
    try {
      const parsed = JSON.parse(text)
      return parsed?.Message || parsed?.message || parsed?.error || noDetails
    } catch {
      return text.length > 280 ? `${text.slice(0, 280)}...` : text
    }
  }

  const refreshUsageCredits = async () => {
    try {
      const { credits, allowance } = await getUsage(orgData)
      setUsageCredits(credits)
      setUsageAllowance(allowance)
      return credits
    } catch (err) {
      console.error('Usage check failed:', err)
      setUsageCredits(0)
      setUsageAllowance(0)
      return 0
    }
  }

  const recordUsage = async (credits) => {
    try {
      const nextCredits = await addUsageCredits(orgData, credits)
      if (nextCredits != null) setUsageCredits(nextCredits)
    } catch (err) {
      console.error('Usage update failed:', err)
    }
  }

  const fetchMergeStats = async () => {
      try {
        const { ok, text } = await callTessituraProcedure({
          ProcedureId: 107,
          ProcedureName: 'kognative_MANAGE_MERGE_PAIR',
          ParameterValues: [{ Name: '@mode', Value: 'merge_stats' }]
        })

        if (!ok) return
        const rows = JSON.parse(text)
        const list = Array.isArray(rows) ? rows : []
        const first = list[0] || {}
        setMergeStats({
          activePairs: Number(first.active_pairs || 0),
          needsReviewPairs: Number(first.needs_review_pairs || 0),
          queuedPairs: Number(first.queued_pairs || 0),
          queuedRecords: Number(first.queued_records || 0),
          mergeAttempts: Number(first.merge_attempts || 0),
          successfulMerges: Number(first.successful_merges || 0),
          failedMerges: Number(first.failed_merges || 0),
          unmerged: Number(first.unmerged || 0),
          lastMergeDt: first.last_merge_dt,
          sources: list
            .filter(row => row.source_created_by)
            .map(row => ({
              name: row.source_created_by,
              count: Number(row.source_merge_count || 0),
              successful: Number(row.source_successful_merges || 0)
            }))
        })
      } catch (err) {
        console.error('Merge stats check failed:', err)
      }
    }

  const fetchAgedStats = async () => {
    setAgedStatsLoading(true)
    try {
      await callTessituraProcedure({
        ProcedureId: 106,
        ProcedureName: 'kognative_AGED_RECORD_REMOVAL',
        ParameterValues: [{ Name: '@mode', Value: 'SETUP' }]
      })

      const { ok, text } = await callTessituraProcedure({
        ProcedureId: 106,
        ProcedureName: 'kognative_AGED_RECORD_REMOVAL',
        ParameterValues: [{ Name: '@mode', Value: 'DATA_AGE' }]
      })

      if (!ok) {
        setAgedStatsLoading(false)
        return
      }
      const rows = JSON.parse(text)
      const first = Array.isArray(rows) ? rows[0] || {} : rows || {}
      setAgedStats({
        avgDataAgeYears: first.avg_data_age_years ?? null,
        oldestDataAgeYears: first.oldest_data_age_years ?? null,
        activeCustomerCount: Number(first.active_customer_count || 0),
        neverActiveCount: Number(first.never_active_count || 0)
      })
    } catch (err) {
      console.error('Aged stats check failed:', err)
    }
    setAgedStatsLoading(false)
  }

  const fetchContactStats = async () => {
    setContactStatsLoading(true)
    try {
      const { ok, text } = await callTessitura('Custom/Execute', 'POST', {
        ProcedureId: 108,
        ProcedureName: 'kognative_CONTACT_SCREEN',
        ParameterValues: [{ Name: '@mode', Value: 'STATS' }]
      })

      if (!ok) {
        setContactStatsLoading(false)
        return
      }
      const rows = JSON.parse(text)
      const first = Array.isArray(rows) ? rows[0] || {} : rows || {}
      const emailDue = Number(first.email_due_count || 0)
      const phoneDue = Number(first.phone_due_count || 0)
      const addressDue = Number(first.address_due_count || 0)
      setContactStats({
        verifiedFreshnessDays: Number(first.verified_freshness_days || 90),
        totalFreshVerifiedCount: Number(first.total_fresh_verified_count || 0),
        totalDueCount: emailDue + phoneDue + addressDue
      })
    } catch (err) {
      console.error('Contact screening stats check failed:', err)
    }
    setContactStatsLoading(false)
  }

  const handleCommitConfidentMerges = async () => {
    setQuickMergeLoading(true)
    setQuickMergeResult(null)
    const readyMergeCount = Number(mergeStats?.activePairs || 0)

    try {
      const identifyResp = await callTessitura('Custom/Execute', 'POST', {
        ProcedureId: 103,
        ProcedureName: 'AP_IDENTIFY_DUPLICATES',
        ParameterValues: [
          { Name: '@changed_since_days', Value: '1095' },
          { Name: '@identify_method1', Value: '1' },
          { Name: '@identify_method2', Value: '2' },
          { Name: '@identify_method3', Value: '3' },
          { Name: '@include_inactive', Value: 'N' },
          { Name: '@list_no', Value: null }
        ]
      })

      if (!identifyResp.ok) {
        setQuickMergeResult({ type: 'error', text: 'Could not identify duplicate candidates.' })
        setQuickMergeLoading(false)
        return
      }

      const promoteResp = await callTessituraProcedure({
        ProcedureId: 104,
        ProcedureName: 'kognative_LP_UPDATE_POSSIBLE_MERGES'
      })

      if (!promoteResp.ok) {
        setQuickMergeResult({ type: 'error', text: getTessituraErrorMessage('Could not promote confident merge pairs.', promoteResp) })
        setQuickMergeLoading(false)
        return
      }

      const mergeResp = await callTessituraProcedure({
        ProcedureId: 105,
        ProcedureName: 'kognative_MERGE_CUSTOMER'
      })

      if (!mergeResp.ok) {
        setQuickMergeResult({ type: 'error', text: getTessituraErrorMessage('Could not commit confident merges.', mergeResp) })
        setQuickMergeLoading(false)
        return
      }

      setQuickMergeResult({ type: 'success', text: 'Confident merges committed.' })
      await fetchMergeStats()
      await recordUsage(readyMergeCount * CREDIT_PRICES.merge)
    } catch (err) {
      setQuickMergeResult({ type: 'error', text: 'Error committing merges: ' + err.message })
    }

    setQuickMergeLoading(false)
  }

  const handleOptimizeIntegrations = async () => {
    setOptimizeLoading(true)
    setOptimizeResult(null)

    try {
      const { ok, status, text } = await callTessitura('Custom/Execute', 'POST', {
        ProcedureId: 107,
        ProcedureName: 'kognative_MANAGE_MERGE_PAIR',
        ParameterValues: [{ Name: '@mode', Value: 'optimize_integrations_run' }]
      })

      if (!ok) {
        setOptimizeResult({ type: 'error', text: `Failed to optimize integrations. Tessitura returned HTTP ${status}.` })
        setOptimizeLoading(false)
        return
      }

      let result = {}
      try {
        const rows = JSON.parse(text)
        result = Array.isArray(rows) ? rows[0] || {} : rows || {}
      } catch {
        setOptimizeResult({ type: 'error', text: 'Could not parse optimize result.' })
        setOptimizeLoading(false)
        return
      }

      const duplicateCount = Number(result.duplicate_login_count || 0)
      setOptimizeResult({
        type: duplicateCount > 0 ? 'warn' : 'success',
        created: Number(result.created_count || 0),
        total: Number(result.candidate_count || 0),
        duplicates: duplicateCount
      })
    } catch (err) {
      setOptimizeResult({ type: 'error', text: 'Error optimizing integrations: ' + err.message })
    }

    setOptimizeLoading(false)
  }

  const access = orgData.access || {}
  const canUseHygiene = access.hygiene
  const canUseScreening = access.screening
  const canUseWealthScreening = access.wealthScreening
  const canUseDemographicScreening = access.demographicScreening
  const canUseSegment = access.segment

  useEffect(() => {
    const checkConnection = async () => {
      try {
        const authString = orgData.organizations.tessitura_auth_string
        const baseUrl = orgData.organizations.tessitura_base_url

        const response = await fetch(`/api/tessitura?endpoint=Diagnostics/Status`, {
          method: 'GET',
          headers: {
            'x-tessitura-auth': authString,
            'x-tessitura-url': baseUrl
          }
        })

        if (response.ok) {
          setApiStatus('connected')
        } else {
          setApiStatus('error')
        }
      } catch (err) {
        console.error('API check failed:', err)
        setApiStatus('error')
      }
    }

    checkConnection()
  }, [orgData])

  useEffect(() => {
    refreshUsageCredits()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgData])

  useEffect(() => {
    if (canUseHygiene) {
      fetchMergeStats()
      fetchAgedStats()
    }
    if (canUseScreening) {
      fetchContactStats()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgData, canUseHygiene, canUseScreening])

  const statusConfig = {
    checking: { color: '#9ca3af', dot: '#9ca3af', label: 'Checking connection...' },
    connected: { color: '#22c55e', dot: '#22c55e', label: 'Tessitura Connected' },
    error: { color: '#f87171', dot: '#f87171', label: 'Connection Failed' }
  }

  const status = statusConfig[apiStatus]
  const sourceColors = ['#16a34a', '#2b6cb0', '#805ad5']
  const usagePercent = usageAllowance > 0
    ? Math.min(100, Math.round((Number(usageCredits || 0) / usageAllowance) * 100))
    : 0
  const usageFillColor = usagePercent >= 100 ? '#dc2626' : usagePercent >= 80 ? '#d97706' : '#1d6fdb'

  const pageLabels = {
    dashboard: 'Home',
    constituentMerge: 'Constituent Merge',
    agedRecordRemoval: 'Aged Record Inactivation',
    screening: 'Contact Screening',
    wealthScreening: 'Wealth Screening',
    demographicScreening: 'Demographic Screening',
    buildSegment: 'Segments',
  }

  const sidebarStyle = isMobile ? {
    ...styles.sidebar,
    position: 'fixed',
    top: 0,
    left: sidebarOpen ? 0 : '-240px',
    height: '100%',
    zIndex: 1000,
    transition: 'left 0.25s ease',
    boxShadow: sidebarOpen ? '4px 0 24px rgba(0,0,0,0.15)' : 'none',
    width: '240px',
  } : styles.sidebar

  useEffect(() => {
    const allowedPages = new Set(['dashboard'])
    if (canUseHygiene) {
      allowedPages.add('constituentMerge')
      allowedPages.add('agedRecordRemoval')
    }
    if (canUseScreening) allowedPages.add('screening')
    if (canUseWealthScreening) allowedPages.add('wealthScreening')
    if (canUseDemographicScreening) allowedPages.add('demographicScreening')
    if (canUseSegment) {
      allowedPages.add('buildSegment')
    }

    if (!allowedPages.has(activePage)) {
      setActivePage('dashboard')
    }
  }, [activePage, canUseDemographicScreening, canUseHygiene, canUseScreening, canUseSegment, canUseWealthScreening])

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes tessBuddyStatPulse {
          0% { opacity: 0.45; transform: scaleX(0.92); }
          50% { opacity: 1; transform: scaleX(1); }
          100% { opacity: 0.45; transform: scaleX(0.92); }
        }
      `}</style>

      {/* Mobile overlay backdrop */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={styles.overlay}
        />
      )}

      {/* Sidebar */}
      <div style={sidebarStyle}>
        <div style={styles.sidebarLogo}>Test Org</div>
        <p style={styles.orgName}>{orgData.organizations.org_name}</p>

        <nav style={styles.nav}>
          <NavItem icon="⊞" label="Home"                 active={activePage === 'dashboard'}        onClick={() => navigateTo('dashboard')} />
          <div style={styles.navSep} />
          {canUseHygiene && (
            <>
              <NavItem icon="🔀" label="Constituent Merge"   active={activePage === 'constituentMerge'} onClick={() => navigateTo('constituentMerge')} />
              <NavItem icon="🗂️" label="Aged Records" active={activePage === 'agedRecordRemoval'} onClick={() => navigateTo('agedRecordRemoval')} />
            </>
          )}
          {canUseScreening && (
            <>
              <div style={styles.navSep} />
              <NavItem icon="✉️" label="Contact Screening"   active={activePage === 'screening'}        onClick={() => navigateTo('screening')} />
            </>
          )}
          {canUseSegment && (
            <>
              <div style={styles.navSep} />
              <NavItem icon="🎯" label="Segments"            active={activePage === 'buildSegment'}     onClick={() => navigateTo('buildSegment')} />
            </>
          )}
          {(canUseWealthScreening || canUseDemographicScreening) && (
            <div style={styles.navAddOns}>
              <div style={styles.navSectionLabel}>Add on features</div>
              {canUseWealthScreening && (
                <NavItem icon="💎" label="Wealth Screening"    active={activePage === 'wealthScreening'}  onClick={() => navigateTo('wealthScreening')} />
              )}
              {canUseDemographicScreening && (
                <NavItem icon="👥" label="Demographic Screening" active={activePage === 'demographicScreening'} onClick={() => navigateTo('demographicScreening')} />
              )}
            </div>
          )}
        </nav>

        <div style={styles.sidebarFooter}>
          <div style={styles.usageBlock}>
            <span style={styles.usageLabel}>Credits Used</span>
            <p style={styles.usageCount}>{usageCredits == null ? 'Loading...' : formatUsageDollars(usageCredits)}</p>
            <div style={styles.usageTrack}>
              <div style={{ ...styles.usageFill, width: `${usagePercent}%`, backgroundColor: usageFillColor }} />
            </div>
            <p style={styles.usageHint}>
              {usageCredits == null
                ? 'Checking usage'
                : usageAllowance > 0
                  ? `${Number(usageCredits || 0).toLocaleString()} of ${usageAllowance.toLocaleString()} credits`
                  : `${Number(usageCredits || 0).toLocaleString()} credits`}
            </p>
          </div>
          <div style={styles.statusRow}>
            <div style={{ ...styles.statusDot, backgroundColor: status.dot }} />
            <span style={{ ...styles.statusLabel, color: status.color, flex: 1 }}>{status.label}</span>
            <button style={styles.logoutInline} onClick={handleLogout}>Sign out</button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div style={{ ...styles.main, padding: isMobile ? '16px' : '48px' }}>

        {/* Mobile top bar */}
        {isMobile && (
          <div style={styles.mobileTopBar}>
            <button style={styles.hamburger} onClick={() => setSidebarOpen(o => !o)} aria-label="Open menu">
              <span style={styles.hamburgerLine} />
              <span style={styles.hamburgerLine} />
              <span style={styles.hamburgerLine} />
            </button>
            <span style={styles.mobilePageTitle}>{pageLabels[activePage] || 'Tess Buddy'}</span>
          </div>
        )}

        <div style={{ ...styles.demoBanner, marginTop: isMobile ? '12px' : '0' }}>
          🎭 Have you ever wanted to click random buttons in Tessitura without the fear of breaking something? Well, go crazy! This is a testing environment.
        </div>

        {activePage === 'dashboard' && (
          <>
            <div style={styles.header}>
              <h1 style={{ ...styles.welcome, fontSize: isMobile ? '22px' : '26px' }}>Welcome back, {orgData.display_name}</h1>
              <p style={styles.role}>{orgData.role}</p>
            </div>

            {canUseHygiene && (
              <div style={styles.section}>
                <p style={styles.sectionChip}>Record Cleaning</p>
                <div style={{ ...styles.cardRow, flexDirection: isMobile ? 'column' : 'row' }}>

                  <div style={styles.dupeStatsCard}>
                    <div>
                      <p style={styles.statLabel}>Duplicate Stats</p>
                      <p style={styles.statValue}>{mergeStats?.successfulMerges ?? '—'}</p>
                      <p style={styles.statHint}>Total constituent merges executed to date</p>
                    </div>
                    <div style={styles.dupeSourceBlock}>
                      <p style={styles.statLabel}>Top Sources</p>
                      <div style={styles.sourceList}>
                        {(mergeStats?.sources?.length ? mergeStats.sources : [{ name: 'No source history yet', count: '—' }]).map((source, index) => (
                          <div key={source.name} style={styles.sourceRow}>
                            <span style={{ ...styles.sourceDot, backgroundColor: sourceColors[index] || '#16a34a' }} />
                            <span style={styles.sourceLabel}>{source.name}</span>
                            <span style={styles.sourceValue}>{source.count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div style={styles.middleActionCard}>
                    <div>
                      <p style={styles.statLabel}>Optimize Integrations</p>
                      <p style={styles.optimizeDesc}>Create missing web logins from primary emails.</p>
                      <button
                        style={{ ...styles.actionButton, opacity: optimizeLoading ? 0.5 : 1, cursor: optimizeLoading ? 'not-allowed' : 'pointer' }}
                        onClick={handleOptimizeIntegrations}
                        disabled={optimizeLoading}
                      >
                        {optimizeLoading ? 'Optimizing...' : 'Optimize'}
                      </button>
                      {optimizeResult && (
                        <div style={{
                          ...styles.optimizeResult,
                          backgroundColor: optimizeResult.type === 'error' ? '#fff5f5' : optimizeResult.type === 'warn' ? '#fffbeb' : '#f8fafd',
                          borderColor: optimizeResult.type === 'error' ? '#feb2b2' : optimizeResult.type === 'warn' ? '#fde68a' : 'rgba(29,111,219,0.12)'
                        }}>
                          <div style={styles.optimizeRow}>
                            <span style={styles.optimizeKey}>{optimizeResult.type === 'error' ? 'Error' : 'Created'}</span>
                            <span style={styles.optimizeVal}>
                              {optimizeResult.type === 'error'
                                ? optimizeResult.text
                                : `${optimizeResult.created}/${optimizeResult.total}${optimizeResult.duplicates ? `, ${optimizeResult.duplicates} skipped` : ''}`}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    <div style={styles.mergeQuickPanel}>
                      <div style={styles.readyPane}>
                        <p style={styles.readyLabel}>Ready to merge</p>
                        <p style={styles.readyValue}>{mergeStats?.activePairs ?? '—'}</p>
                      </div>
                      <div style={styles.commitPane}>
                        <p style={styles.readyLabel}>Execute Merges</p>
                        <button
                          style={{ ...styles.commitQuickButton, opacity: quickMergeLoading ? 0.55 : 1, cursor: quickMergeLoading ? 'not-allowed' : 'pointer' }}
                          onClick={handleCommitConfidentMerges}
                          disabled={quickMergeLoading}
                        >
                          {quickMergeLoading ? 'Merging...' : 'Merge'}
                        </button>
                        {quickMergeResult && (
                          <p style={{
                            ...styles.quickMergeResult,
                            color: quickMergeResult.type === 'error' ? '#dc2626' : '#16a34a'
                          }}>
                            {quickMergeResult.text}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div style={styles.scheduleHomeCard}>
                    <ScheduleAutoMergeTool orgData={orgData} toolCardMode fillCard />
                  </div>

                </div>
              </div>
            )}

            {(canUseHygiene || canUseScreening) && (
              <div style={styles.section}>
              <div style={{ ...styles.homeMetricGrid, gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, minmax(0, 1fr))' }}>
                {canUseHygiene && (
                  <div style={isMobile ? styles.homeMetricChipSlot : { ...styles.homeMetricChipSlot, gridColumn: '1 / span 2' }}>
                    <p style={styles.sectionChip}>Aged Record Inactivation</p>
                  </div>
                )}
                {canUseScreening && (
                  <div style={isMobile ? styles.homeMetricChipSlot : { ...styles.homeMetricChipSlot, gridColumn: canUseHygiene ? '3 / span 2' : '1 / span 2' }}>
                    <p style={styles.sectionChip}>Contact Screening</p>
                  </div>
                )}

                  {canUseHygiene && (
                    <>
                      <button
                        type="button"
                        style={styles.agedHomeCard}
                        onClick={() => navigateTo('agedRecordRemoval')}
                      >
                        <p style={styles.statLabel}>Average Data Age</p>
                        <p style={styles.statValue}>
                          {agedStatsLoading && !agedStats ? <LoadingStatValue /> : agedStats?.avgDataAgeYears ?? '—'}
                        </p>
                        <p style={styles.statHint}>
                          Years since latest activity across {agedStats?.activeCustomerCount ? agedStats.activeCustomerCount.toLocaleString() : 'active'} active records
                        </p>
                      </button>

                      <button
                        type="button"
                        style={styles.agedHomeCard}
                        onClick={() => navigateTo('agedRecordRemoval')}
                      >
                        <p style={styles.statLabel}>Oldest Active Record</p>
                        <p style={styles.statValue}>
                          {agedStatsLoading && !agedStats ? <LoadingStatValue /> : agedStats?.oldestDataAgeYears ?? '—'}
                        </p>
                        <p style={styles.statHint}>
                          Uses customer activity, latest contribution, latest ticket, and create date fallback
                        </p>
                      </button>
                    </>
                  )}

                  {canUseScreening && (
                    <>
                      <button
                        type="button"
                        style={styles.agedHomeCard}
                        onClick={() => navigateTo('screening')}
                      >
                        <p style={styles.statLabel}>Recently Verified</p>
                        <p style={styles.statValue}>
                          {contactStatsLoading && !contactStats ? <LoadingStatValue /> : contactStats?.totalFreshVerifiedCount?.toLocaleString() ?? '—'}
                        </p>
                        <p style={styles.statHint}>
                          Contact points verified in the last {contactStats?.verifiedFreshnessDays || 90} days
                        </p>
                      </button>

                      <button
                        type="button"
                        style={styles.agedHomeCard}
                        onClick={() => navigateTo('screening')}
                      >
                        <p style={styles.statLabel}>Need Screening</p>
                        <p style={styles.statValue}>
                          {contactStatsLoading && !contactStats ? <LoadingStatValue /> : contactStats?.totalDueCount?.toLocaleString() ?? '—'}
                        </p>
                        <p style={styles.statHint}>
                          Email, phone, and address contact points due for validation
                        </p>
                      </button>
                    </>
                  )}
              </div>
            </div>
            )}
          </>
        )}
        {activePage === 'constituentMerge' && canUseHygiene && (
          <ConstituentMerge orgData={orgData} onUsageUpdated={refreshUsageCredits} />
        )}
        {activePage === 'agedRecordRemoval' && canUseHygiene && (
          <AgedRecordRemoval orgData={orgData} />
        )}
        {activePage === 'screening' && canUseScreening && (
          <Screening orgData={orgData} onUsageUpdated={refreshUsageCredits} />
        )}
        {activePage === 'wealthScreening' && canUseWealthScreening && (
          <WealthScreening />
        )}
        {activePage === 'demographicScreening' && canUseDemographicScreening && (
          <DemographicScreening />
        )}
        {activePage === 'buildSegment' && canUseSegment && (
          <BuildSegment orgData={orgData} onUsageUpdated={refreshUsageCredits} />
        )}
      </div>
    </div>
  )
}

const styles = {
  container: { display: 'flex', height: '100vh', background: '#f0f7ff' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 999 },
  sidebar: { width: '220px', backgroundColor: 'white', borderRight: '1px solid rgba(29,111,219,0.1)', padding: '20px 12px', display: 'flex', flexDirection: 'column', flexShrink: 0, boxShadow: '2px 0 16px rgba(29,111,219,0.05)' },
  logo: { color: '#0c1a33', fontSize: '20px', fontWeight: '700', margin: 0 },
  orgName: { color: '#b0bac5', fontSize: '11px', fontWeight: '500', letterSpacing: '0.3px', textAlign: 'center', margin: '0 4px 16px', fontFamily: "'Inter', sans-serif", whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  sidebarLogo: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '72px', padding: '12px', margin: '0 auto 8px', borderRadius: '10px', color: '#0c1a33', fontFamily: "'Space Grotesk', sans-serif", fontSize: '20px', fontWeight: '700', letterSpacing: '-0.02em', textAlign: 'center', background: 'linear-gradient(135deg, rgba(29,111,219,0.08), rgba(56,189,248,0.12))', border: '1px solid rgba(29,111,219,0.14)' },
  nav: { flex: 1, display: 'flex', flexDirection: 'column' },
  navSep: { height: '1px', background: 'rgba(29,111,219,0.07)', margin: '6px 4px' },
  navAddOns: { marginTop: 'auto', paddingTop: '12px' },
  navSectionLabel: { color: '#9ca3af', fontSize: '10px', fontWeight: '700', letterSpacing: '0.7px', textTransform: 'uppercase', margin: '0 10px 6px', fontFamily: "'Inter', sans-serif" },
  sidebarFooter: { borderTop: '1px solid rgba(29,111,219,0.07)', paddingTop: '12px' },
  statusRow: { display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 2px' },
  statusDot: { width: '7px', height: '7px', borderRadius: '50%', flexShrink: 0 },
  statusLabel: { fontSize: '11px', fontWeight: '500', fontFamily: "'Inter', sans-serif" },
  logoutInline: { background: 'none', border: 'none', color: '#9ca3af', fontSize: '11px', fontFamily: "'Inter', sans-serif", cursor: 'pointer', padding: '2px 4px', borderRadius: '4px', flexShrink: 0 },
  mobileTopBar: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' },
  hamburger: { background: 'none', border: 'none', cursor: 'pointer', padding: '8px', display: 'flex', flexDirection: 'column', gap: '5px', borderRadius: '8px', flexShrink: 0 },
  hamburgerLine: { display: 'block', width: '22px', height: '2px', background: '#0c1a33', borderRadius: '2px' },
  mobilePageTitle: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '17px', fontWeight: '600', color: '#0c1a33', letterSpacing: '-0.3px' },
  demoBanner: { background: 'linear-gradient(135deg, rgba(29,111,219,0.08), rgba(56,189,248,0.08))', border: '1px solid rgba(29,111,219,0.2)', borderRadius: '10px', padding: '10px 16px', marginBottom: '20px', fontSize: '13px', color: '#1d6fdb', fontFamily: "'Inter', sans-serif" },
  main: { flex: 1, padding: '48px', overflowY: 'auto', background: '#f0f7ff', minWidth: 0 },
  header: { marginBottom: '32px' },
  welcome: { fontSize: '26px', fontWeight: '700', color: '#0c1a33', marginBottom: '4px', fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.02em' },
  role: { fontSize: '13px', color: '#4b5563', textTransform: 'capitalize', fontFamily: "'Inter', sans-serif" },
  infoCard: { backgroundColor: 'white', borderRadius: '12px', padding: '24px', marginBottom: '24px', maxWidth: '500px', boxShadow: '0 2px 16px rgba(29,111,219,0.08)', border: '1px solid rgba(29,111,219,0.1)' },
  infoLabel: { fontSize: '11px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#9ca3af', marginBottom: '4px', marginTop: '16px', fontFamily: "'Inter', sans-serif" },
  infoValue: { fontSize: '14px', color: '#0c1a33', fontWeight: '500', fontFamily: "'Inter', sans-serif" },
  section: { marginBottom: '36px' },
  sectionChip: { display: 'inline-block', fontSize: '10px', fontWeight: '700', color: '#1d6fdb', background: 'rgba(29,111,219,0.07)', border: '1px solid rgba(29,111,219,0.15)', borderRadius: '100px', padding: '3px 10px', letterSpacing: '0.04em', textTransform: 'uppercase', fontFamily: "'Inter', sans-serif", marginBottom: '14px' },
  cardRow: { display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'stretch' },
  statCard: { backgroundColor: 'white', borderRadius: '12px', padding: '20px', flex: '1 1 280px', minWidth: '240px', minHeight: '240px', boxSizing: 'border-box', boxShadow: '0 2px 16px rgba(29,111,219,0.08)', border: '1px solid rgba(29,111,219,0.1)' },
  middleActionCard: { backgroundColor: 'white', borderRadius: '12px', padding: '20px', flex: '1 1 280px', minWidth: '240px', minHeight: '240px', boxSizing: 'border-box', boxShadow: '0 2px 16px rgba(29,111,219,0.08)', border: '1px solid rgba(29,111,219,0.1)', display: 'flex', flexDirection: 'column', gap: '18px' },
  dupeStatsCard: { backgroundColor: 'white', borderRadius: '12px', padding: '20px', flex: '1 1 280px', minWidth: '240px', minHeight: '240px', boxSizing: 'border-box', boxShadow: '0 2px 16px rgba(29,111,219,0.08)', border: '1px solid rgba(29,111,219,0.1)', display: 'flex', flexDirection: 'column', gap: '16px' },
  dupeSourceBlock: { borderTop: '1px solid rgba(29,111,219,0.1)', paddingTop: '14px', minWidth: 0 },
  scheduleHomeCard: { flex: '1 1 280px', minWidth: '240px', minHeight: '240px', display: 'flex' },
  homeMetricGrid: { display: 'grid', gap: '16px', alignItems: 'stretch' },
  homeMetricChipSlot: { alignSelf: 'end' },
  agedStatsGrid: { display: 'grid', gap: '16px', alignItems: 'stretch' },
  agedHomeCard: { backgroundColor: 'white', borderRadius: '12px', padding: '22px', minHeight: '160px', height: '100%', boxSizing: 'border-box', boxShadow: '0 2px 16px rgba(29,111,219,0.08)', border: '1px solid rgba(29,111,219,0.1)', textAlign: 'left', cursor: 'pointer', fontFamily: "'Inter', sans-serif" },
  statLabel: { fontSize: '11px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#9ca3af', marginBottom: '8px', fontFamily: "'Inter', sans-serif" },
  statValue: { fontSize: '32px', fontWeight: '700', color: '#0c1a33', marginBottom: '4px', fontFamily: "'Space Grotesk', sans-serif" },
  loadingStatValue: { display: 'inline-block', width: '72px', height: '32px', borderRadius: '8px', background: 'linear-gradient(135deg, rgba(29,111,219,0.12), rgba(56,189,248,0.28))', transformOrigin: 'left center', animation: 'tessBuddyStatPulse 1.1s ease-in-out infinite' },
  statHint: { fontSize: '12px', color: '#9ca3af', marginTop: '8px', fontFamily: "'Inter', sans-serif" },
  sourceList: { display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '4px' },
  sourceRow: { display: 'flex', alignItems: 'center', gap: '8px' },
  sourceDot: { width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#16a34a', flexShrink: 0 },
  sourceLabel: { fontSize: '13px', color: '#4b5563', flex: 1, fontFamily: "'Inter', sans-serif" },
  sourceValue: { fontSize: '14px', fontWeight: '600', color: '#0c1a33', fontFamily: "'Inter', sans-serif" },
  optimizeDesc: { fontSize: '13px', color: '#4b5563', lineHeight: '1.5', marginBottom: '10px', fontFamily: "'Inter', sans-serif" },
  optimizeResult: { backgroundColor: '#f8fafd', borderRadius: '8px', border: '1px solid rgba(29,111,219,0.12)', padding: '10px 14px', marginBottom: '4px' },
  optimizeRow: { display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #f0f3f8' },
  optimizeKey: { fontSize: '12px', color: '#6b7280', fontFamily: "'Inter', sans-serif" },
  optimizeVal: { fontSize: '12px', fontWeight: '600', color: '#0c1a33', fontFamily: "'Inter', sans-serif" },
  actionButton: { width: '100%', padding: '10px', background: 'linear-gradient(135deg, #1d6fdb, #38bdf8)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', fontFamily: "'Inter', sans-serif", cursor: 'pointer' },
  mergeQuickPanel: { marginTop: 'auto', borderTop: '1px solid rgba(29,111,219,0.1)', paddingTop: '16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', alignItems: 'stretch', minHeight: '118px' },
  readyPane: { borderRight: '1px solid rgba(29,111,219,0.1)', paddingRight: '14px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', minHeight: '100px' },
  readyLabel: { fontSize: '11px', fontWeight: '700', letterSpacing: '0.04em', textTransform: 'uppercase', color: '#9ca3af', marginBottom: '6px', fontFamily: "'Inter', sans-serif" },
  readyValue: { fontSize: '32px', fontWeight: '700', color: '#0c1a33', fontFamily: "'Space Grotesk', sans-serif", lineHeight: 1 },
  commitPane: { display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '8px', minWidth: 0 },
  commitQuickButton: { width: '100%', minHeight: '42px', padding: '9px 10px', background: 'linear-gradient(135deg, #c53030, #ef4444)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '700', fontFamily: "'Inter', sans-serif", cursor: 'pointer', boxShadow: '0 2px 12px rgba(197,48,48,0.18)' },
  quickMergeResult: { margin: 0, fontSize: '11px', lineHeight: '1.35', fontWeight: '600', fontFamily: "'Inter', sans-serif" },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', maxWidth: '700px' },
  card: { backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 16px rgba(29,111,219,0.08)', border: '1px solid rgba(29,111,219,0.1)' },
  cardTitle: { fontSize: '16px', fontWeight: '700', color: '#0c1a33', marginBottom: '8px', fontFamily: "'Space Grotesk', sans-serif" },
  cardDesc: { fontSize: '13px', color: '#4b5563', lineHeight: '1.6', fontFamily: "'Inter', sans-serif" },
  usageBlock: { margin: '16px 0 12px', padding: '12px 10px', background: 'rgba(29,111,219,0.04)', borderRadius: '10px', border: '1px solid rgba(29,111,219,0.1)' },
  usageLabel: { fontSize: '10px', fontWeight: '700', letterSpacing: '0.05em', textTransform: 'uppercase', color: '#9ca3af', fontFamily: "'Inter', sans-serif" },
  usageCount: { fontSize: '13px', fontWeight: '700', color: '#0c1a33', fontFamily: "'Inter', sans-serif", margin: '6px 0 8px' },
  usageTrack: { height: '6px', background: 'rgba(29,111,219,0.1)', borderRadius: '100px', overflow: 'hidden' },
  usageFill: { height: '100%', minWidth: '2px', borderRadius: '100px', transition: 'width 0.4s ease, background-color 0.2s ease' },
  usageHint: { fontSize: '10px', color: '#9ca3af', marginTop: '6px', fontFamily: "'Inter', sans-serif" }
}

export default Dashboard
